import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, Check, Sparkles } from 'lucide-react';
import { usePathScope } from '../context/PathScopeContext';
import { toast } from 'sonner';

/**
 * "More tools you may like", permanently visible in the sidebar so no feature
 * is ever discovered by accident. Out-of-path tools are switched off, never
 * hidden: one tap adds them to this person's navigation for good.
 */
export const ToolboxPanel = () => {
  const { hasScope, optionalModules, toggleModule } = usePathScope();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');

  const items = optionalModules || [];
  if (!hasScope || items.length === 0) return null;

  const off = items.filter(m => !m.on);
  const added = items.filter(m => m.on);

  const flip = async (m, on) => {
    setBusy(m.key);
    try {
      await toggleModule(m.key, on);
      toast.success(on ? `${m.label} added to your menu` : `${m.label} removed from your menu`);
    } catch {
      toast.error('Could not update your tools');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-4 px-1" data-testid="toolbox-panel">
      <button onClick={() => setOpen(o => !o)} data-testid="toolbox-toggle"
        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
        <Sparkles className="w-4 h-4 text-cyan-300 shrink-0" />
        <span className="flex-1 text-sm font-medium">More tools you may like</span>
        <span className="text-[10px] text-slate-300">{off.length}</span>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="mt-1 space-y-1 pl-1">
          <p className="text-[11px] text-slate-300 px-2 pb-1">
            Not part of your path, so they're switched off. Turn on anything you want in your menu.
          </p>

          {added.length > 0 && (
            <div className="pb-1">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300 px-2 py-1">Added by you</p>
              {added.map(m => (
                <div key={m.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5">
                  <Link to={m.path} className="flex-1 text-xs text-slate-200 hover:text-white truncate" data-testid={`toolbox-link-${m.key}`}>
                    {m.label}
                  </Link>
                  <button onClick={() => flip(m, false)} disabled={busy === m.key} data-testid={`toolbox-off-${m.key}`}
                    className="text-[10px] text-slate-300 hover:text-rose-300 shrink-0">remove</button>
                </div>
              ))}
            </div>
          )}

          {off.map(m => (
            <div key={m.key} className="px-2 py-1.5 rounded-lg hover:bg-white/5" data-testid={`toolbox-item-${m.key}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-200 truncate">{m.label}</p>
                  {m.pitch && <p className="text-[10px] text-slate-300 leading-snug">{m.pitch}</p>}
                </div>
                <button onClick={() => flip(m, true)} disabled={busy === m.key} data-testid={`toolbox-on-${m.key}`}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-cyan-400/40 text-[10px] text-cyan-200 hover:bg-cyan-500/10">
                  {busy === m.key ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} Turn on
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolboxPanel;
